import { z } from "zod";
import { BlockReason } from "../blocklist.ts";
import { Decision, IsoDateTime, OperatorEnvironment, PlayerRef } from "../common.ts";
import { ReasonCodeSchema } from "../reasons.ts";

// Building blocks of the `explorer/v1` contracts (design D4): strict query objects (unknown keys are
// rejected, mass assignment), capped pagination and the shapes shared by several routes.

/** A repeatable query parameter (`?operator_id=a&operator_id=b`) or a single value, as an array. */
export function repeatable<T extends z.ZodType>(item: T, max: number) {
  return z
    .union([item, z.array(item).min(1).max(max)])
    .transform((value): z.output<T>[] => (Array.isArray(value) ? value : [value]));
}

// No Explorer route takes an `environment` parameter (ADR 0011, design D6 of
// bind-operator-environment-to-installation): every route serves only the installation's
// operator environment, and the strict query and body schemas refuse a leftover `environment` key
// with `400 invalid_request` naming the field.

/** UTC day `YYYY-MM-DD` (the unit of the daily read models); periods are inclusive days. */
export const IsoDay = z.iso.date().meta({ id: "IsoDay" });

/** UTC instant (`2026-09-30T03:00:00.000Z`; `Z` only, never an offset or a local time). */
export const IsoInstant = z.iso.datetime().meta({ id: "IsoInstant" });

/**
 * One bound of a query period (owner decision 2026-10-01): a UTC day — `from` is its first instant,
 * `to` includes the whole day — or a UTC instant — `from` inclusive, `to` exclusive. The console
 * sends the midnights of the user's display time zone as instants, so "today" or "last 30 days"
 * are the user's days, not UTC days.
 */
export const PeriodBound = z.union([IsoDay, IsoInstant]).meta({ id: "PeriodBound" });

/**
 * A resolved period as the API echoes it: the instants `[since, until)` it covered and, for
 * clients that work in days, the UTC days of its first and last instant.
 */
export const PeriodV1 = z
  .object({ from: IsoDay, to: IsoDay, since: IsoDateTime, until: IsoDateTime })
  .strict()
  .meta({ id: "PeriodV1" });
export type PeriodV1 = z.infer<typeof PeriodV1>;

/** At most this many operators in an `operator_id` filter. */
export const MAX_OPERATOR_FILTER = 20;
export const OperatorFilter = repeatable(z.uuid(), MAX_OPERATOR_FILTER);

/** Failure reasons filter (verification reason codes). */
export const MAX_REASON_FILTER = 10;
export const ReasonFilter = repeatable(ReasonCodeSchema, MAX_REASON_FILTER);

/** Page-based lists: 20 rows per page by default, at most 100. */
export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;
export const MAX_PAGE = 500;
export const PageParams = {
  page: z.coerce.number().int().min(1).max(MAX_PAGE).default(1),
  size: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
};

/** Opaque cursor of cursor-paginated lists (base64url of the last row's sort key). */
export const Cursor = z
  .string()
  .min(1)
  .max(256)
  .regex(/^[A-Za-z0-9_-]+$/)
  .meta({ id: "Cursor" });

/** Transaction lists: 50 rows by default; the player/device map asks for up to 5,000 at once. */
export const DEFAULT_TRANSACTION_LIMIT = 50;
export const MAX_TRANSACTION_LIMIT = 5000;

/** Current block status of a player or device (derived from active block entries). */
export const SubjectStatusV1 = z
  .object({
    status: z.enum(["none", "blocked"]),
    /** Present when blocked: the active block. */
    block: z
      .object({
        block_id: z.uuid(),
        reason: BlockReason,
        blocked_at: IsoDateTime,
        expires_at: IsoDateTime.optional(),
      })
      .strict()
      .optional(),
  })
  .strict()
  .meta({ id: "SubjectStatusV1" });
export type SubjectStatusV1 = z.infer<typeof SubjectStatusV1>;

/** A count that may be an estimate ("~42 Users"): `approximate` tells the UI to show the tilde. */
export const ApproximateCountV1 = z
  .object({ value: z.int().nonnegative(), approximate: z.boolean() })
  .strict()
  .meta({ id: "ApproximateCountV1" });

/**
 * Position of a transaction on a map (primary source; browser only in v0). `precise` only for
 * `location.precise.read` within retention (audited); otherwise the center of the precision-5
 * geohash. `approximate_location` is true when the precise coordinates no longer exist (retention).
 */
export const PositionV1 = z
  .object({
    precision: z.enum(["precise", "geohash5"]),
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    geohash5: z.string().regex(/^[0-9b-hjkmnp-z]{5}$/),
    /** Accuracy radius of the precise position (m); precise only. */
    accuracy_m: z.number().nonnegative().optional(),
    /** Accuracy radius of the source (km), kept in the long tier. */
    accuracy_km: z.number().nonnegative().optional(),
    approximate_location: z.boolean(),
  })
  .strict()
  .meta({ id: "PositionV1" });
export type PositionV1 = z.infer<typeof PositionV1>;

/** Card / table row of a transaction (user, device and IP transactions). */
export const TransactionItemV1 = z
  .object({
    verification_id: z.uuid(),
    operator_id: z.uuid(),
    occurred_at: IsoDateTime,
    decision: Decision,
    reason_codes: z.array(z.string()),
    player_ref: PlayerRef,
    device_ref: z.uuid().nullable(),
    /** Null after the 180-day tier. */
    ip: z.string().nullable(),
    ip_country: z.string().nullable(),
    sdk: z.object({ name: z.string(), version: z.string() }).strict(),
    product: z.string(),
    purpose: z.string(),
    primary_source: z.string(),
    country: z.string().nullable(),
    state: z.string().nullable(),
    /** IBGE code of the municipality (kept for search and exports). */
    municipality: z.string().nullable(),
    /** Name of the municipality (e.g. "Rio de Janeiro"); null when unknown: show the code. */
    municipality_name: z.string().nullable(),
    /** Null when the transaction has no position (located by IP country only in v0). */
    position: PositionV1.nullable(),
  })
  .strict()
  .meta({ id: "TransactionItemV1" });
export type TransactionItemV1 = z.infer<typeof TransactionItemV1>;

/** Blocklist entry as the Explorer shows it (identifier, reason, comment, author, dates). */
export const BlockEntryV1 = z
  .object({
    block_id: z.uuid(),
    operator_id: z.uuid(),
    environment: OperatorEnvironment,
    target_kind: z.enum(["player", "device"]),
    target_ref: z.string(),
    reason: BlockReason,
    comment: z.string(),
    expires_at: IsoDateTime.nullable(),
    status: z.enum(["active", "unblocked", "expired"]),
    blocked_by: z.uuid(),
    blocked_at: IsoDateTime,
    /** Console user id, or `system` for an expiry. */
    unblocked_by: z.string().nullable(),
    unblock_reason: z.string().nullable(),
    unblocked_at: IsoDateTime.nullable(),
  })
  .strict()
  .meta({ id: "BlockEntryV1" });
export type BlockEntryV1 = z.infer<typeof BlockEntryV1>;
